/**
 * Клиентский трекер событий объявлений (аналитика, docs/analytics-api-contract.md §Events).
 *
 * - Пока VITE_TRACKING_ENABLED != "true" — транспорт no-op (в dev — лог в консоль).
 * - Батч раз в FLUSH_MS и при уходе со страницы (visibilitychange → sendBeacon).
 * - Анонимный visitorId в localStorage, без персональных данных.
 * - Никогда не бросает исключений и ничего не ждёт в рендере.
 */

export type TrackSource =
  | "search"
  | "home"
  | "map"
  | "top"
  | "vip"
  | "urgent"
  | "instagram"
  | "direct"
  | "share"
  | "other";

export type TrackEventName =
  | "listing_impression"
  | "listing_view"
  | "contact_whatsapp_click"
  | "contact_call_click"
  | "contact_phone_reveal"
  | "contact_message_start"
  | "favorite_add"
  | "favorite_remove"
  | "map_open"
  | "share_click";

export interface TrackEvent {
  name: TrackEventName;
  listingId: number;
  /** UTC ISO. */
  at: string;
  source?: TrackSource;
  /** Позиция карточки в списке (0-based), для listing_impression. */
  position?: number;
  promotionType?: "top" | "vip" | "urgent";
  abTestId?: string;
  abVariantId?: "A" | "B";
}

export interface TrackBatch {
  visitorId: string;
  /** Вошедший пользователь (для исключения просмотров владельца); null — гость. */
  userId: number | null;
  /** Путь страницы, где собраны события. */
  page: string;
  events: TrackEvent[];
}

export interface TrackTransport {
  send(batch: TrackBatch, opts: { unloading: boolean }): void;
}

const FLUSH_MS = 10_000;
const MAX_QUEUE = 200;
const VISITOR_KEY = "turaq-visitor-id";

const enabled = import.meta.env.VITE_TRACKING_ENABLED === "true";

function endpoint(): string {
  try {
    return `${new URL(import.meta.env.VITE_API_BASE_URL || "").origin}/api/analytics/events`;
  } catch {
    return "/api/analytics/events";
  }
}

const httpTransport: TrackTransport = {
  send(batch, { unloading }) {
    const body = JSON.stringify(batch);
    try {
      if (unloading && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon(endpoint(), new Blob([body], { type: "application/json" }));
        return;
      }
      void fetch(endpoint(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      /* трекинг не должен ломать страницу */
    }
  },
};

const noopTransport: TrackTransport = {
  send(batch) {
    if (import.meta.env.DEV) console.debug("[track]", batch.events.map((e) => `${e.name}#${e.listingId}${e.source ? `@${e.source}` : ""}`).join(" "), batch);
  },
};

let transport: TrackTransport = enabled ? httpTransport : noopTransport;
let queue: TrackEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let userIdGetter: () => number | null = () => null;
let visitorId: string | null = null;

/** Контекст страницы объявления — для контактов, которые не знают id объявления. */
let pageContext: Pick<TrackEvent, "listingId" | "source" | "abTestId" | "abVariantId"> | null = null;

/** Показы, уже отправленные на этой странице: `${path}|${listingId}`. */
const seenImpressions = new Set<string>();

function getVisitorId(): string {
  if (visitorId) return visitorId;
  try {
    visitorId = localStorage.getItem(VISITOR_KEY);
    if (!visitorId) {
      visitorId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VISITOR_KEY, visitorId);
    }
  } catch {
    visitorId = visitorId || "anonymous";
  }
  return visitorId;
}

function flush(unloading = false): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  try {
    transport.send(
      { visitorId: getVisitorId(), userId: safeUserId(), page: location.pathname, events },
      { unloading },
    );
  } catch {
    /* ignore */
  }
}

function safeUserId(): number | null {
  try {
    return userIdGetter();
  } catch {
    return null;
  }
}

function schedule(): void {
  if (!timer) timer = setTimeout(() => flush(false), FLUSH_MS);
}

let listening = false;
function listen(): void {
  if (listening || typeof document === "undefined") return;
  listening = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => flush(true));
}

const NAMES: readonly TrackEventName[] = [
  "listing_impression", "listing_view", "contact_whatsapp_click", "contact_call_click", "contact_phone_reveal",
  "contact_message_start", "favorite_add", "favorite_remove", "map_open", "share_click",
];
const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;

/** Только известные значения: в батч не попадает ничего постороннего (и никаких персональных данных). */
function clean(event: Omit<TrackEvent, "at">): Omit<TrackEvent, "at"> | null {
  if (!NAMES.includes(event.name)) return null;
  if (!Number.isSafeInteger(event.listingId) || event.listingId <= 0) return null;
  const out: Omit<TrackEvent, "at"> = { name: event.name, listingId: event.listingId };
  if (event.source && KNOWN.includes(event.source)) out.source = event.source;
  if (Number.isSafeInteger(event.position) && (event.position as number) >= 0) out.position = event.position;
  if (event.promotionType && ["top", "vip", "urgent"].includes(event.promotionType)) out.promotionType = event.promotionType;
  if (event.abTestId && SAFE_ID.test(event.abTestId) && (event.abVariantId === "A" || event.abVariantId === "B")) {
    out.abTestId = event.abTestId;
    out.abVariantId = event.abVariantId;
  }
  return out;
}

function push(raw: Omit<TrackEvent, "at">): void {
  try {
    const event = clean(raw);
    if (!event) return;
    listen();
    queue.push({ ...event, at: new Date().toISOString() });
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    schedule();
  } catch {
    /* ignore */
  }
}

export const analyticsTracker = {
  /** Событие с явным объявлением. */
  track(name: TrackEventName, data: Omit<TrackEvent, "at" | "name">): void {
    push({ name, ...data });
  },

  /** Событие на странице объявления (контакты, карта, избранное, «поделиться»). */
  trackOnPage(name: TrackEventName, listingId?: number | null): void {
    const ctx = pageContext;
    const id = Number(listingId ?? ctx?.listingId);
    if (!id) return;
    const sameListing = ctx && ctx.listingId === id;
    push({
      name,
      listingId: id,
      ...(sameListing ? { source: ctx.source, abTestId: ctx.abTestId, abVariantId: ctx.abVariantId } : {}),
    });
  },

  /** Карточка видна ≥50% ≥1 c — один раз на страницу. */
  impression(data: Omit<TrackEvent, "at" | "name">): void {
    try {
      const key = `${location.pathname}|${data.listingId}`;
      if (seenImpressions.has(key)) return;
      seenImpressions.add(key);
    } catch {
      /* ignore */
    }
    push({ name: "listing_impression", ...data });
  },

  setPageContext(ctx: typeof pageContext): void {
    pageContext = ctx;
  },

  setUserIdGetter(fn: () => number | null): void {
    userIdGetter = fn;
  },

  /** Для тестов и будущего batch-endpoint. */
  setTransport(next: TrackTransport): void {
    transport = next;
  },

  flush,
};

/** Источник просмотра по текущему пути (лента/поиск/карта/главная). */
export function sourceFromPath(pathname: string): TrackSource {
  const p = pathname.replace(/^\/(ru|kg|en|kz|uz|tr|zh)(?=\/|$)/, "") || "/";
  if (p === "/" || p === "/home") return "home";
  if (p.startsWith("/listings") || p.includes("/nedvizhimost")) return "search";
  if (p.startsWith("/kyrgyz-map") || p.startsWith("/map")) return "map";
  return "other";
}

const KNOWN: readonly TrackSource[] = ["search", "home", "map", "top", "vip", "urgent", "instagram", "direct", "share", "other"];

/**
 * Источник открытия страницы объявления: state.src (внутренняя ссылка) →
 * ?src= → utm_source=instagram → referrer (свой сайт — other, чужой — other,
 * нет — direct).
 */
export function resolveViewSource(state: unknown, search: string, referrer: string): TrackSource {
  try {
    const fromState = (state as { src?: string } | null)?.src;
    if (fromState && KNOWN.includes(fromState as TrackSource)) return fromState as TrackSource;
    const q = new URLSearchParams(search);
    const src = q.get("src");
    if (src && KNOWN.includes(src as TrackSource)) return src as TrackSource;
    const utm = (q.get("utm_source") || "").toLowerCase();
    if (utm.includes("instagram") || /instagram\.com|l\.instagram/.test(referrer)) return "instagram";
    if (q.get("utm_medium") === "share") return "share";
    if (!referrer) return "direct";
    return "other";
  } catch {
    return "other";
  }
}
